import React, { memo, useCallback } from 'react';
import PropTypes from 'prop-types';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';

const SearchBar = ({
  value,
  onChange,
  onCancelSearch,
  onRequestSearch,
  placeholder,
  disabled,
  autoFocus,
  fullWidth,
  className,
  style,
  inputProps,
}) => {
  const handleChange = useCallback(
    (event) => {
      if (onChange) {
        onChange(event.target.value);
      }
    },
    [onChange]
  );

  const handleClear = useCallback(() => {
    if (onChange) {
      onChange('');
    }

    if (onCancelSearch) {
      onCancelSearch();
    }
  }, [onChange, onCancelSearch]);

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'Enter' && onRequestSearch) {
        onRequestSearch(value);
      }

      if (event.key === 'Escape') {
        handleClear();
      }
    },
    [value, onRequestSearch, handleClear]
  );

  return (
    <Paper
      className={className}
      style={style}
      elevation={1}
      sx={{
        display: 'flex',
        alignItems: 'center',
        width: fullWidth ? '100%' : undefined,
        boxSizing: 'border-box',
      }}
    >
      <TextField
        value={value || ''}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        autoFocus={autoFocus}
        fullWidth={fullWidth}
        variant="standard"
        slotProps={{
          input: {
            disableUnderline: true,
            endAdornment: value ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label="Clear search"
                  onClick={handleClear}
                  disabled={disabled}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : (
              <InputAdornment position="start" sx={{ pl: 1 }}>
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          },
          htmlInput: inputProps,
        }}
        sx={{
          px: 1,
          py: 0.5,
          boxSizing: 'border-box',
        }}
      />
    </Paper>
  );
};

SearchBar.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  onCancelSearch: PropTypes.func,
  onRequestSearch: PropTypes.func,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  autoFocus: PropTypes.bool,
  fullWidth: PropTypes.bool,
  className: PropTypes.string,
  style: PropTypes.object,
  inputProps: PropTypes.object,
};

SearchBar.defaultProps = {
  value: '',
  placeholder: 'Search',
  disabled: false,
  autoFocus: false,
  fullWidth: true,
};

export default memo(SearchBar);
